<template>
  <div class="yms-category-tree-panel">
    <Tree
      :data="treeData"
      :render="renderTreeNode"
      children-key="children"
      class="yms-category-tree"
    />
  </div>
</template>

<script>
export default {
  name: 'CategoryTreePanel',
  props: {
    /**
     * 原始树数据
     */
    sourceData: {
      type: Array,
      default: () => []
    },

    /**
     * 显示字段
     */
    labelKey: {
      type: String,
      default: 'title'
    },

    /**
     * 默认唯一值字段
     */
    valueKey: {
      type: String,
      default: 'ymsProductCategoryId'
    },

    /**
     * 子级字段
     */
    childrenKey: {
      type: String,
      default: 'children'
    },

    /**
     * 默认选中值
     */
    defaultSelectedValue: {
      type: [String, Number],
      default: ''
    }
  },

  data() {
    return {
      treeData: [],
      currentSelectedValue: ''
    };
  },

  watch: {
    sourceData: {
      handler(val) {
        this.initTreeData(val || []);
      },
      immediate: true,
      deep: true
    },
    defaultSelectedValue: {
      handler(val) {
        if (val !== '' && val !== null && val !== undefined) {
          this.currentSelectedValue = String(val);
          this.refreshSelectedState(this.treeData);
        }
      },
      immediate: true
    }
  },

  methods: {
    /**
     * 初始化树数据
     * 默认全部收起，只显示第一级
     */
    initTreeData(list = []) {
      this.treeData = this.formatTreeData(list, 0);
      this.collapseAll(this.treeData);

      if (this.defaultSelectedValue !== '' && this.defaultSelectedValue !== null && this.defaultSelectedValue !== undefined) {
        this.currentSelectedValue = String(this.defaultSelectedValue);
        this.refreshSelectedState(this.treeData);
      }
    },

    /**
     * 格式化树数据
     */
    formatTreeData(list = [], level = 0) {
      return (list || []).map(item => {
        const children = Array.isArray(item[this.childrenKey]) ? item[this.childrenKey] : [];
        const title = item[this.labelKey] || item.name || item.title || '';

        return {
          ...item,
          title,
          expand: false,
          selected: false,
          __level: level,
          __value: item[this.valueKey],
          __title: title,
          children: this.formatTreeData(children, level + 1)
        };
      });
    },

    /**
     * 全部收起
     */
    collapseAll(list = []) {
      (list || []).forEach(item => {
        this.$set(item, 'expand', false);
        if (item.children && item.children.length > 0) {
          this.collapseAll(item.children);
        }
      });
    },

    /**
     * 刷新选中状态
     */
    refreshSelectedState(list = []) {
      (list || []).forEach(item => {
        this.$set(item, 'selected', String(item.__value) === String(this.currentSelectedValue));
        if (item.children && item.children.length > 0) {
          this.refreshSelectedState(item.children);
        }
      });
    },

    /**
     * 是否有子节点
     */
    hasChildren(node) {
      return !!(node.children && node.children.length);
    },

    /**
     * 只负责选中
     */
    handleSelect(node) {
      this.currentSelectedValue = String(node.__value);
      this.refreshSelectedState(this.treeData);
      this.$emit('change', node);
      this.$emit('input', node.__value);
      this.$emit('select', node);
    },

    /**
     * 只负责展开/收起
     */
    handleToggleExpand(node) {
      if (!this.hasChildren(node)) return;
      this.$set(node, 'expand', !node.expand);
      this.$emit('toggle', node);
    },

    /**
     * 渲染箭头
     * 顶级右侧：默认向下，展开向上
     * 二级及以下左侧：默认向右，展开向下
     */
    renderArrow(h, node, position = 'left') {
      const hasChildren = this.hasChildren(node);
      const isTop = position === 'right';

      let iconType = '';

      if (hasChildren) {
        if (isTop) {
          iconType = node.expand ? 'ios-arrow-up' : 'ios-arrow-down';
        } else {
          iconType = node.expand ? 'ios-arrow-down' : 'ios-arrow-forward';
        }
      }

      return h(
        'span',
        {
          class: [
            'yms-tree-arrow',
            isTop ? 'yms-tree-arrow-right' : 'yms-tree-arrow-left',
            !hasChildren ? 'is-hidden' : ''
          ],
          on: hasChildren
            ? {
              click: e => {
                e.stopPropagation();
                e.preventDefault();
                this.handleToggleExpand(node);
              },
              mousedown: e => {
                e.stopPropagation();
                e.preventDefault();
              }
            }
            : {}
        },
        [
          hasChildren
            ? h('Icon', {
              props: {
                type: iconType,
                size: isTop ? 17 : 16,
                color: '#aaaaaa'
              },
            })
            : ''
        ]
      );
    },

    /**
     * 自定义渲染节点
     */
    renderTreeNode(h, {data}) {
      const level = data.__level || 0;
      const isSelected = String(this.currentSelectedValue) === String(data.__value);
      const isTopLevel = level === 0;
      const isSecondLevel = level === 1;

      const rightArrow = isTopLevel ? this.renderArrow(h, data, 'right') : null;

      const leftArrow = !isTopLevel
        ? this.renderArrow(h, data, 'left')
        : h('span', {class: 'yms-tree-arrow-placeholder'});

      return h(
        'div',
        {
          class: [
            'yms-tree-node',
            `level-${level}`,
            isTopLevel ? 'yms-tree-node-top' : 'yms-tree-node-child',
            isSelected ? 'is-selected' : ''
          ],
        },
        [
          isTopLevel
            ? h(
              'div',
              {
                class: 'yms-tree-node-top-inner'
              },
              [
                h(
                  'span',
                  {
                    class: [
                      'yms-tree-node-label',
                      'yms-tree-node-label-level-1'
                    ],
                    attrs: {
                      title: data.__title
                    },
                    on: {
                      click: e => {
                        e.stopPropagation();
                        this.handleSelect(data);
                      }
                    }
                  },
                  data.__title
                ),
                rightArrow
              ]
            )
            : h(
              'div',
              {
                class: 'yms-tree-node-child-inner'
              },
              [
                leftArrow,
                h(
                  'span',
                  {
                    class: [
                      'yms-tree-node-label',
                      isSecondLevel ? 'yms-tree-node-label-level-2' : 'yms-tree-node-label-level-3-plus'
                    ],
                    attrs: {
                      title: data.__title
                    },
                    on: {
                      click: e => {
                        e.stopPropagation();
                        this.handleSelect(data);
                      }
                    }
                  },
                  data.__title
                )
              ]
            )
        ]
      );
    }
  }
};
</script>

<style>
.yms-category-tree-panel {
  width: 280px;
  height: 326px;
  background-color: #fff;
  border-radius-bottomleft: 8px;
  border-radius-bottomright: 8px;
  box-sizing: border-box;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 15px;
  box-sizing: border-box;
}

.yms-category-tree-panel .ivu-tree li ul {
  padding-left: 0;
}

.yms-category-tree-panel::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.yms-category-tree {
  padding: 8px 0;
}

/* 隐藏 Tree 默认箭头 */
.yms-category-tree-panel .ivu-tree-arrow {
  display: none !important;
}

/* 去掉 iView 默认 title 样式 */
.yms-category-tree-panel .ivu-tree-title {
  display: block;
  width: 100%;
  padding: 0 !important;
  background: transparent !important;
}

.yms-category-tree-panel .ivu-tree-title:hover,
.yms-category-tree-panel .ivu-tree-title-selected {
  background: transparent !important;
}

.yms-category-tree-panel .ivu-tree ul li {
  margin: 0;
}

.yms-category-tree-panel .ivu-tree-children {
  overflow: hidden;
}

/* 节点通用 */
.yms-tree-node {
  width: 100%;
  box-sizing: border-box;
  color: #333;
}

/* 一级节点 */
.yms-tree-node-top {
  min-height: 40px;
  line-height: 40px;
  border-bottom: 1px solid #ececec;
}

.yms-tree-node-top-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
}

/* 二级及以下节点 */
.yms-tree-node-child {
  min-height: 35px;
  line-height: 35px;
  padding-right: 5px;
}

.yms-tree-node-child-inner {
  display: flex;
  align-items: center;
  min-height: 35px;
}

/* 二级：轻微缩进 */
.yms-tree-node.level-1 {
  padding-left: 0;
}

/* 三级：再缩一点 */
.yms-tree-node.level-2 {
  padding-left: 15px;
}

/* 四级以上 */
.yms-tree-node.level-3 {
  padding-left: 15px;
}

/* 箭头 */
.yms-tree-arrow,
.yms-tree-arrow-placeholder {
  width: 30px;
  min-width: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #666;
  cursor: pointer;
  user-select: none;
}

.yms-tree-arrow-right {
  width: 36px;
  min-width: 36px;
}

.yms-tree-arrow.is-hidden {
  visibility: hidden;
  cursor: default;
}

/* 名称 */
.yms-tree-node-label {
  flex: 1;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  cursor: pointer;
}

/* 一级分类名称：黑色高亮加粗 */
.yms-tree-node-label-level-1 {
  color: #000;
  font-size: 14px;
}

/* 二级分类名称：黑色高亮加粗 */
.yms-tree-node-label-level-2 {
  color: #333;
  font-size: 13px;
}

/* 三级及以下 */
.yms-tree-node-label-level-3-plus {
  color: #333;
  font-size: 13px;
}

/* hover */
.yms-tree-node-label:hover,
.yms-tree-arrow:hover {
  color: #89b92a;
  font-weight: bold;
}

/* 选中 */
.yms-tree-node.is-selected .yms-tree-node-label,
.yms-tree-node.is-selected .yms-tree-arrow {
  color: #89b92a !important;
}
</style>